import {
  type Query,
  type QueryClient,
  matchQuery,
  noop,
  queryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { type StaticDecode, Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import { ReadonlyObject } from "@/shared/schema/schema.ts";
import { FetchError, httpChecked } from "@/shared/util/fetch.ts";
import { getCvPartner } from "@/shared/util/getCvPartner.ts";

const schema = Type.Union([
  ReadonlyObject({ logged_in: Type.Literal(false) }),
  ReadonlyObject({
    logged_in: Type.Literal(true),
    session_expires_in_minutes: Type.Number(),
  }),
]);

export type LoggedInStatus = StaticDecode<typeof schema>;
const checker = TypeCompiler.Compile(schema);

const loggedInStatusQuery = queryOptions({
  queryKey: ["logged_in_status"] as const,
  staleTime: 10_000,
  queryFn: ({ signal }) =>
    httpChecked.get("/api/v2/logged_in_status", checker, {
      signal,
      skipLoadingIndicator: true,
      timeout: 5000,
    }),
});

const queries = {
  check: loggedInStatusQuery,
};

// Exported so tests can seed a logged-out session deterministically, rather than
// hand-typing the query key.
export const loggedInStatusQueryKey = queries.check.queryKey;

const filters = { queryKey: queries.check.queryKey, exact: true };

export function isLoggedInStatusQuery(query: Query<unknown, unknown>) {
  return matchQuery(filters, query);
}

interface Session {
  lostAt: number | undefined;
}

const sessions = new WeakMap<QueryClient, Session>();

// Anchors the loss at the poll or probe that first saw it, so a page the
// redirect failed to unload reports its errors again once the window passes.
export function trackLoggedInStatus(client: QueryClient) {
  const session: Session = { lostAt: undefined };
  sessions.set(client, session);

  const unsubscribe = client.getQueryCache().subscribe(({ query }) => {
    if (!isLoggedInStatusQuery(query)) return;

    const { data } = query.state;
    if (!checker.Check(data)) return;

    session.lostAt = data.logged_in
      ? undefined
      : (session.lostAt ?? Date.now());
  });

  return () => {
    unsubscribe();
    sessions.delete(client);
  };
}

// Rails answers a dead session with a 401, the API gateway with a 404.
const sessionLostStatuses = new Set([401, 404]);
const sessionLostWindow = 30_000;
// A 401 is unambiguous, so the cache is trusted only long enough to fold a burst.
const unauthorizedStaleTime = 100;

export async function sessionWasLost(client: QueryClient, error: unknown) {
  if (!getCvPartner().isLoggedIn) return false;
  if (!(error instanceof FetchError)) return false;
  if (!sessionLostStatuses.has(error.status)) return false;

  const session = sessions.get(client);
  if (!session) return false;

  try {
    const status =
      error.status === 401
        ? await client.query({
            ...queries.check,
            staleTime: unauthorizedStaleTime,
          })
        : await client.query(queries.check);
    if (status.logged_in) return false;
  } catch {
    return false;
  }

  if (session.lostAt == null) return false;

  return Date.now() - session.lostAt < sessionLostWindow;
}

export function useWillBeLoggedOut() {
  const { data } = useQuery({
    ...queries.check,
    refetchInterval: 60_000,
    select: (loggedInStatus) => {
      if (!loggedInStatus.logged_in) {
        return false;
      }

      const sessionExpiresInMinutes = loggedInStatus.session_expires_in_minutes;

      return sessionExpiresInMinutes <= 10;
    },
  });

  return data;
}

export function useIsLoggedIn(poll = false) {
  const { data } = useQuery({
    ...queries.check,
    refetchInterval: poll ? 60_000 : undefined,
    // A session expiring while the tab is hidden must still redirect on
    // schedule, not wait for a refocus that fires every other query at once.
    refetchIntervalInBackground: true,
    select: (loggedInStatus) => loggedInStatus.logged_in,
  });

  return data;
}

export async function prefetchLoggedInStatus(client: QueryClient) {
  await client
    .query({ ...queries.check, staleTime: Infinity })
    // eslint-disable-next-line unicorn/prefer-await
    .catch(noop);
}

export function usePostponeLogout() {
  return useMutation({
    mutationKey: ["postpone_logout"],
    mutationFn: (postponedExpiryTime: boolean) =>
      httpChecked.post("/api/v2/logged_in_status", checker, {
        body: {
          logged_in_status: { postponed_expiry_time: postponedExpiryTime },
        },
      }),
    // ideally, we would use onSuccess and populate the cache, but the data
    // returned by the server is stale (`session_expires_in_minutes` is not
    // updated). It's still here as the backend might get fixed at some point
    // (and the data is more up to date, technically), but we still invalidate.
    onSuccess: (data, _vars, _onMutateResult, { client }) => {
      client.setQueryData(queries.check.queryKey, data);
    },
    onSettled: (_result, _error, _vars, _onMutateResult, { client }) =>
      client.invalidateQueries(queries.check),
  });
}
